<script setup>
import { ref, computed, watch } from 'vue';
import AccordionItem from 'dashboard/components/Accordion/AccordionItem.vue';
import { useIqRecommendations } from 'dashboard/composables/useIqRecommendations';

import Button from '../../../components-next/button/Button.vue';
import ConversationMergeModal from './ConversationMergeModal.vue';

const props = defineProps({
  currentChat: {
    required: true,
    type: Object,
  },
});

const emit = defineEmits(['insertText', 'close']);

const {
  isIqStreamLoading,
  iqRecommendation,
  sentimentScores,
  eqRecommendation,
  streamingError,
} = useIqRecommendations(computed(() => props.currentChat));

const activeTab = ref('agentic'); // 'agentic' or 'profile'

// State for accordion items - set to default open for the main sections
const accordionStates = ref({
  iq_recommendation: true,
  eq_recommendation: true,
  rlhfl: false,
  sentiment_analysis: true,
});

const toggleAccordion = key => {
  accordionStates.value[key] = !accordionStates.value[key];
};

const insertRecommendation = text => {
  if (text) {
    emit('insertText', text);
  }
};

const selectedIQOption = ref('');
const selectedEQOption = ref('');

const selectIQOption = option => {
  selectedIQOption.value = option;
  selectedEQOption.value = ''; // Deselect EQ option when IQ option is selected
  insertRecommendation(option);
};

const selectEQOption = option => {
  selectedEQOption.value = option;
  selectedIQOption.value = ''; // Deselect IQ option when EQ option is selected
  insertRecommendation(option);
};

const rlhflSelectedOption = ref('');
const isModalOpen = ref(false);
const chartData = ref([]);
// Chart sizing
const CHART_W = 360;
const CHART_H = 160;
const AXIS_X = 24; // Y-axis line x (moved right to avoid label clipping)
const AXIS_TOP = 6;
const AXIS_BOTTOM = CHART_H - 6;
const GRID_LEFT = 36; // start grid a bit right of axis
const INNER_LEFT = GRID_LEFT; // chart drawing starts at grid left
const INNER_RIGHT = CHART_W - 8; // right padding
const TOP_PADDING = 10;
const BOTTOM_PADDING = 10;

const updateChartData = () => {
  if (sentimentScores.value && sentimentScores.value.length > 0) {
    chartData.value = sentimentScores.value.map((score, index) => {
      return {
        time: `${index}`,
        value: score,
      };
    });
    return;
  }

  const sentiment = props.currentChat?.recommendations?.sentiment;
  if (sentiment && sentiment.score !== undefined) {
    const finalScore = sentiment.score;

    if (Array.isArray(finalScore)) {
      chartData.value = finalScore.map((score, index) => {
        return {
          time: `${index}`,
          value: score,
        };
      });
      return;
    }

    chartData.value = [{ time: '0', value: finalScore }];
    return;
  }
  chartData.value = [];
};

watch(
  sentimentScores,
  () => {
    updateChartData();
  },
  { deep: true, immediate: true }
);

const sentimentChartData = computed(() => chartData.value);
updateChartData();

const selectRLHFOption = option => {
  rlhflSelectedOption.value = option;
};

const submitForApproval = () => {
  // Enable the button regardless of selection for testing
  isModalOpen.value = true;
};

const closeModal = () => {
  isModalOpen.value = false;
};

const transformYValue = value => {
  // Map [-1,1] into [TOP_PADDING, CHART_H - BOTTOM_PADDING]
  const minY = TOP_PADDING;
  const maxY = CHART_H - BOTTOM_PADDING;
  const midY = (minY + maxY) / 2;
  const amplitude = (maxY - minY) / 2;
  return midY - value * amplitude;
};

const generateChartPath = data => {
  if (data.length === 0) return '';

  if (data.length === 1) {
    const y = transformYValue(data[0].value);
    return `M ${INNER_LEFT},${y} L ${INNER_RIGHT},${y}`;
  }

  const availableWidth = Math.max(1, INNER_RIGHT - INNER_LEFT);
  const segmentWidth = availableWidth / Math.max(1, data.length - 1);

  const points = data.map((point, index) => {
    const x = INNER_LEFT + index * segmentWidth;
    const y = transformYValue(point.value);
    return `${x},${y}`;
  });

  return `M ${points.join(' L ')}`;
};

// Helpers to build positive (green) and negative (red) area fills around baseline (value 0)
const buildPoints = data => {
  if (data.length === 0) return [];
  const availableWidth = Math.max(1, INNER_RIGHT - INNER_LEFT);
  const step = availableWidth / Math.max(1, data.length - 1);
  return data.map((p, i) => ({
    x: INNER_LEFT + i * step,
    y: transformYValue(p.value),
    v: p.value,
  }));
};

const zeroY = transformYValue(0);
const intersectWithZero = (p0, p1) => {
  const dv = p1.v - p0.v;
  const t = dv === 0 ? 0 : (0 - p0.v) / dv;
  const x = p0.x + t * (p1.x - p0.x);
  return { x, y: zeroY, v: 0 };
};

const splitSegments = (points, positive = true) => {
  const segments = [];
  let current = [];
  for (let i = 0; i < points.length - 1; i += 1) {
    const a = points[i];
    const b = points[i + 1];
    const aOk = positive ? a.v >= 0 : a.v <= 0;
    const bOk = positive ? b.v >= 0 : b.v <= 0;

    if (aOk) {
      if (current.length === 0) current.push(a);
      else current.push(a);
    }

    if (aOk && bOk) {
      current.push(b);
      if (i === points.length - 2) {
        segments.push(current);
      }
    } else if (a.v === 0 && !bOk) {
      // starting at zero then going out of region – close minimal segment
      current.push(a);
      if (current.length > 1) segments.push(current);
      current = [];
    } else if (!aOk && bOk) {
      // entering region – start at intersection
      const I = intersectWithZero(a, b);
      current = [I, b];
      if (i === points.length - 2) segments.push(current);
    } else if (aOk && !bOk) {
      // leaving region – end at intersection
      const I = intersectWithZero(a, b);
      current.push(I);
      if (current.length > 1) segments.push(current);
      current = [];
    }
  }
  return segments;
};

const buildAreaPath = (segments, baselineY) => {
  if (!segments || segments.length === 0) return '';
  let d = '';
  segments.forEach(seg => {
    if (!seg || seg.length === 0) return;
    const first = seg[0];
    const last = seg[seg.length - 1];
    d += `M ${first.x},${first.y}`;
    for (let i = 1; i < seg.length; i += 1) {
      d += ` L ${seg[i].x},${seg[i].y}`;
    }
    d += ` L ${last.x},${baselineY} L ${first.x},${baselineY} Z`;
  });
  return d;
};

const generatePositiveAreaPath = data => {
  const pts = buildPoints(data);
  const segments = splitSegments(pts, true);
  return buildAreaPath(segments, zeroY);
};

const generateNegativeAreaPath = data => {
  const pts = buildPoints(data);
  const segments = splitSegments(pts, false);
  return buildAreaPath(segments, zeroY);
};

// Y-axis emoji ticks on the left side (outside chart)
const yEmojiTicks = computed(() => [
  { value: 1.0, label: '1.0' }, // happiest
  { value: 0.5, label: '0.5' }, // happier
  { value: 0.0, label: '0.0' }, // neutral
  { value: -0.5, label: '-0.5' }, // sadder
  { value: -1.0, label: '-1.0' }, // saddest
]);
</script>

<template>
  <div
    class="ltr:border-l rtl:border-r border-n-weak h-full overflow-hidden z-10 w-[320px] min-w-[320px] 2xl:min-w-[360px] 2xl:w-[360px] flex flex-col bg-n-background"
  >
    <div
      class="flex items-center justify-between px-4 py-2 border-b border-n-weak h-12"
    >
      <div class="flex items-center justify-between gap-2 flex-1">
        <span class="font-medium text-sm text-n-slate-12">
          {{ $t('AGENTIC_ASSISTANCE.TAB.AGENTIC') }}
        </span>
        <div class="flex items-center">
          <Button
            v-for="button in buttons"
            :key="button.key"
            v-tooltip="button.tooltip"
            :icon="button.icon"
            ghost
            sm
          />
          <Button
            v-tooltip="$t('GENERAL.CLOSE')"
            icon="i-lucide-x"
            class="text-n-slate-12 h-6 w-6"
            ghost
            size="lg"
            @click="$emit('close')"
          />
        </div>
      </div>
    </div>

    <!-- Tab Content -->
    <div class="flex-1 overflow-auto">
      <div v-if="activeTab === 'agentic'" class="py-2 space-y-1">
        <!-- IQ Recommendation Section -->
        <AccordionItem
          :title="$t('AGENTIC_ASSISTANCE.IQ_RECOMMENDATION.TITLE')"
          :is-open="accordionStates.iq_recommendation"
          compact
          @toggle="toggleAccordion('iq_recommendation')"
        >
          <div class="p-3">
            <!-- Loading state -->
            <div
              v-if="isIqStreamLoading"
              class="flex items-center space-x-2 p-3 bg-n-background-weak border border-n-weak rounded-lg"
            >
              <div
                class="animate-spin rounded-full h-5 w-5 border-b-2 border-n-q-foreground border"
              />
              <span class="text-sm text-n-text">{{
                $t('AGENTIC_ASSISTANCE.IQ_RECOMMENDATION.LOADING')
              }}</span>
            </div>

            <!-- Error State -->
            <div
              v-if="streamingError && !isIqStreamLoading"
              class="p-1 bg-n-background-weak border border-n-weak rounded-lg"
            >
              <div class="flex items-center space-x-2">
                <svg
                  class="w-5 h-5 text-red-500"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clip-rule="evenodd"
                  />
                </svg>
                <span class="text-xs text-n-text">{{
                  $t('AGENTIC_ASSISTANCE.IQ_RECOMMENDATION.NO_DATA')
                }}</span>
              </div>
            </div>

            <!-- IQ Recommendation -->
            <div v-if="!isIqStreamLoading && iqRecommendation">
              <div
                class="w-full p-2 border rounded-lg cursor-pointer transition-all duration-200 shadow-sm hover:shadow-md"
                :class="
                  selectedIQOption === iqRecommendation
                    ? 'border-n-q-foreground border bg-n-background-strong shadow-woot-100'
                    : 'border-n-weak bg-n-background-weak hover:border-n-q-foreground border hover:bg-n-background-strong'
                "
                @click="selectIQOption(iqRecommendation)"
              >
                <div class="flex-1 min-w-0">
                  <p
                    class="text-xs text-n-text font-medium leading-[1.125rem]"
                    :class="
                      selectedIQOption === iqRecommendation
                        ? 'text-woot-600 dark:text-white'
                        : 'text-n-text'
                    "
                  >
                    {{
                      iqRecommendation ||
                      $t('AGENTIC_ASSISTANCE.IQ_RECOMMENDATION.NO_DATA')
                    }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </AccordionItem>

        <!-- EQ Recommendation Section -->
        <AccordionItem
          :title="$t('AGENTIC_ASSISTANCE.EQ_RECOMMENDATION.TITLE')"
          :is-open="accordionStates.eq_recommendation"
          compact
          @toggle="toggleAccordion('eq_recommendation')"
        >
          <!-- <template #button>
            <button
              v-tooltip="
                $t('AGENTIC_ASSISTANCE.EQ_RECOMMENDATION.REFRESH_TOOLTIP')
              "
              class="mr-2 p-1 rounded hover:bg-n-slate-3 transition-colors"
              :class="{ 'animate-spin': isEqLoading }"
              :disabled="isEqLoading"
              @click.stop="fetchEqRecommendations"
            >
              <fluent-icon
                size="16"
                icon="arrow-clockwise"
                class="text-n-slate-11"
              />
            </button>
          </template> -->

          <div class="p-3 space-y-3">
            <!-- Loading State -->
            <div
              v-if="isIqStreamLoading"
              class="flex items-center space-x-2 p-3 bg-n-background-weak border border-n-weak rounded-lg"
            >
              <div
                class="animate-spin rounded-full h-5 w-5 border-b-2 border-n-q-foreground border"
              />
              <span class="text-sm text-n-text">{{
                $t('AGENTIC_ASSISTANCE.EQ_RECOMMENDATION.LOADING')
              }}</span>
            </div>

            <!-- Error State -->
            <div
              v-if="streamingError && !isIqStreamLoading"
              class="p-3 bg-n-background-weak border border-n-weak rounded-lg"
            >
              <div class="flex items-center space-x-2">
                <svg
                  class="w-5 h-5 text-red-500"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clip-rule="evenodd"
                  />
                </svg>
                <span class="text-xs text-n-text">{{
                  $t('AGENTIC_ASSISTANCE.EQ_RECOMMENDATION.NO_DATA')
                }}</span>
              </div>
              <button
                class="mt-2 text-xs text-woot-600 hover:text-woot-800 underline"
                @click="fetchEqRecommendations"
              >
                {{ $t('AGENTIC_ASSISTANCE.EQ_RECOMMENDATION.RETRY') }}
              </button>
            </div>

            <!-- EQ Options -->
            <div v-if="!isIqStreamLoading && eqRecommendation.length > 0">
              <div v-for="(option, index) in eqRecommendation" :key="index">
                <div
                  class="mb-2 w-full p-2 border rounded-lg cursor-pointer transition-all duration-200 shadow-sm hover:shadow-md"
                  :class="
                    selectedEQOption === option
                      ? 'border-n-q-foreground text-n-q-foreground border bg-n-background-strong shadow-woot-100'
                      : 'border-n-weak bg-n-background-weak hover:border-n-q-foreground border hover:bg-n-background-strong'
                  "
                  @click="selectEQOption(option)"
                >
                  <div class="flex items-start space-x-2">
                    <!-- Option number badge -->
                    <div
                      class="flex-shrink-0 w-6 h-8 rounded-full flex items-center justify-center text-xs font-medium"
                      :class="
                        selectedEQOption === option
                          ? 'bg-woot-600 text-white'
                          : 'bg-n-weak text-n-text-muted'
                      "
                    >
                      {{ index + 1 }}
                    </div>

                    <!-- Content -->
                    <div class="flex-1 min-w-0">
                      <p
                        class="text-xs text-n-text font-medium leading-[1.125rem]"
                        :class="
                          selectedEQOption === option
                            ? 'text-n-q-foreground'
                            : 'text-xs text-n-text'
                        "
                      >
                        {{ option }}
                      </p>
                    </div>

                    <!-- <div
                      v-if="selectedEQOption === option"
                      class="flex-shrink-0"
                    >
                      <div
                        class="w-5 h-5 bg-woot-600 rounded-full flex items-center justify-center"
                      >
                        <svg
                          class="w-3 h-3 text-white"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fill-rule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clip-rule="evenodd"
                          />
                        </svg>
                      </div>
                    </div> -->
                  </div>
                </div>
              </div>
            </div>

            <!-- No recommendations state -->
            <div
              v-if="
                !isIqStreamLoading &&
                !streamingError &&
                eqRecommendation.length === 0
              "
              class="p-3 bg-n-background-weak border border-n-weak rounded-lg text-center"
            >
              <span class="text-sm text-n-text-muted">{{
                $t('AGENTIC_ASSISTANCE.EQ_RECOMMENDATION.NO_RECOMMENDATIONS')
              }}</span>
              <button
                class="block mt-2 text-sm text-woot-600 hover:text-woot-800 underline"
                @click="fetchEqRecommendations"
              >
                {{ $t('AGENTIC_ASSISTANCE.EQ_RECOMMENDATION.GENERATE') }}
              </button>
            </div>
          </div>
        </AccordionItem>

        <!-- RLHFL Section -->
        <AccordionItem
          :title="$t('AGENTIC_ASSISTANCE.RLHFL.TITLE')"
          :is-open="accordionStates.rlhfl"
          compact
          @toggle="toggleAccordion('rlhfl')"
        >
          <div class="px-3 space-y-4">
            <div class="rounded-lg bg-n-q-background p-2">
              <!-- Two option cards with arrow -->
              <div class="flex items-center gap-3">
                <div
                  class="flex-1 p-2 cursor-pointer transition-all duration-200"
                  :class="
                    rlhflSelectedOption === 'quant_ai'
                      ? 'border-n-q-foreground border bg-n-background-strong'
                      : 'border-n-weak bg-n-background-weak hover:border-n-strong'
                  "
                  @click="selectRLHFOption('quant_ai')"
                >
                  <h4 class="text-center font-medium text-n-text">
                    {{ $t('AGENTIC_ASSISTANCE.RLHFL.QUANT_AI') }}
                  </h4>
                </div>

                <!-- Arrow icon -->
                <div class="flex-shrink-0">
                  <svg
                    width="22"
                    height="23"
                    viewBox="0 0 22 23"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M2.33789 6.5C4.06694 3.51099 7.29866 1.5 11.0001 1.5C16.5229 1.5 21.0001 5.97715 21.0001 11.5C21.0001 17.0228 16.5229 21.5 11.0001 21.5C7.29866 21.5 4.06694 19.489 2.33789 16.5M11 15.5L15 11.5M15 11.5L11 7.5M15 11.5H1"
                      stroke="rgb(var(--slate-12))"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </div>

                <div
                  class="flex-1 p-2 cursor-pointer transition-all duration-200"
                  :class="
                    rlhflSelectedOption === 'human_agent'
                      ? 'border-n-q-foreground border bg-n-background-strong'
                      : 'border-n-weak bg-n-background-weak hover:border-n-strong'
                  "
                  @click="selectRLHFOption('human_agent')"
                >
                  <h4 class="text-center font-medium text-n-text">
                    {{ $t('AGENTIC_ASSISTANCE.RLHFL.HUMAN_AGENT') }}
                  </h4>
                </div>
              </div>

              <!-- Submit button -->
              <button
                class="w-full px-4 py-2 rounded-lg font-normal transition-colors border-solid border-n-q-foreground border text-n-q-foreground dark:bg-n-black mt-2"
                @click="submitForApproval"
              >
                {{ $t('AGENTIC_ASSISTANCE.RLHFL.SUBMIT') }}
              </button>
            </div>
          </div>
        </AccordionItem>

        <!-- Sentiment Analysis Section -->
        <AccordionItem
          :title="$t('AGENTIC_ASSISTANCE.SENTIMENT.TITLE')"
          :is-open="accordionStates.sentiment_analysis"
          compact
          @toggle="toggleAccordion('sentiment_analysis')"
        >
          <div class="p-3">
            <div>
              <!-- Line + Area chart with positive (green) and negative (red) fills -->
              <div v-if="sentimentChartData.length > 0" class="w-full relative">
                <svg
                  class="w-full h-full"
                  :viewBox="`0 0 ${CHART_W} ${CHART_H}`"
                >
                  <!-- Y Axis with emoji labels (outside chart content) -->
                  <g>
                    <!-- Axis line -->
                    <line
                      :x1="AXIS_X"
                      :y1="AXIS_TOP"
                      :x2="AXIS_X"
                      :y2="AXIS_BOTTOM"
                      stroke="#9ca3af"
                      stroke-width="1"
                    />
                    <!-- Ticks, grid and emoji labels -->
                    <g v-for="(tick, i) in yEmojiTicks" :key="i">
                      <line
                        :x1="AXIS_X"
                        :y1="transformYValue(tick.value)"
                        :x2="GRID_LEFT"
                        :y2="transformYValue(tick.value)"
                        stroke="#9ca3af"
                        stroke-width="1"
                      />
                      <text
                        :x="4"
                        :y="transformYValue(tick.value) + 3"
                        font-size="10"
                        fill="#60646c"
                        text-anchor="start"
                      >
                        {{ tick.label }}
                      </text>
                      <!-- faint grid to the right only -->
                      <line
                        :x1="GRID_LEFT"
                        :y1="transformYValue(tick.value)"
                        :x2="CHART_W"
                        :y2="transformYValue(tick.value)"
                        stroke="#9ca3af"
                        stroke-dasharray="2,2"
                        stroke-width="1"
                      />
                    </g>
                  </g>
                  <defs>
                    <linearGradient
                      id="posArea"
                      x1="0"
                      y1="0"
                      :x2="0"
                      :y2="CHART_H"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop
                        offset="0%"
                        stop-color="#16a34a"
                        stop-opacity="0.28"
                      />
                      <stop
                        offset="100%"
                        stop-color="#16a34a"
                        stop-opacity="0.10"
                      />
                    </linearGradient>
                    <linearGradient
                      id="negArea"
                      x1="0"
                      y1="0"
                      :x2="0"
                      :y2="CHART_H"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop
                        offset="0%"
                        stop-color="#ef4444"
                        stop-opacity="0.28"
                      />
                      <stop
                        offset="100%"
                        stop-color="#ef4444"
                        stop-opacity="0.10"
                      />
                    </linearGradient>
                  </defs>

                  <!-- Negative area (below zero) -->
                  <path
                    :d="generateNegativeAreaPath(sentimentChartData)"
                    fill="url(#negArea)"
                    stroke="none"
                  />

                  <!-- Positive area (above zero) -->
                  <path
                    :d="generatePositiveAreaPath(sentimentChartData)"
                    fill="url(#posArea)"
                    stroke="none"
                  />

                  <!-- Generate path for line chart -->
                  <path
                    :d="generateChartPath(sentimentChartData)"
                    fill="none"
                    stroke="#9ca3af"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>

              <div
                v-else
                class="w-full h-10 text-sm justify-center text-n-text-muted"
              >
                {{ $t('AGENTIC_ASSISTANCE.SENTIMENT.NO_DATA') }}
              </div>

              <!-- Chart legend -->
              <div
                v-if="sentimentChartData.length > 0"
                class="flex justify-between text-xs text-n-text-muted mt-2"
              >
                <span>{{ $t('AGENTIC_ASSISTANCE.SENTIMENT.NEGATIVE') }}</span>
                <span>{{ $t('AGENTIC_ASSISTANCE.SENTIMENT.NEUTRAL') }}</span>
                <span>{{ $t('AGENTIC_ASSISTANCE.SENTIMENT.POSITIVE') }}</span>
              </div>
            </div>
          </div>
        </AccordionItem>
      </div>
    </div>
  </div>

  <!-- Conversation Merge Modal -->
  <ConversationMergeModal
    :is-open="isModalOpen"
    :conversation-id="currentChat.id"
    @close="closeModal"
  />
</template>

<style scoped>
.accordion-item {
  border-bottom: 1px solid var(--n-weak);
}
rlhf-bg {
  background-color: #2839f8;
}
</style>
